// src/components/ContactModal.tsx
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Mail,
  AlertCircle,
  Send,
  CheckCircle,
  XCircle,
  MessageSquare,
  Bug,
  Lightbulb,
  HelpCircle
} from "lucide-react";
import { createContactQuery } from "@/api/service/contact-queries";
import { useApi } from "@/hooks/useApi";

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("Other");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState<"success" | "error" | null>(null);

  const contactQueryApi = useApi(createContactQuery);

  useEffect(() => {
    const user = localStorage.getItem("gt-user");
    if (user) {
      try {
        const parsed = JSON.parse(user);
        setEmail(parsed.email || "");
      } catch (error) {
        console.error("Failed to parse user from localStorage");
      }
    }
  }, []);

  const handleSubmit = async () => {
    try {
      await contactQueryApi.execute({
        email,
        type: category,
        message: description,
      });
      setSubmitted("success");
    } catch {
      setSubmitted("error");
    }
  };

  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        setSubmitted(null);
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [submitted, onClose]);

  useEffect(() => {
    if (isOpen && !submitted) {
      setDescription("");
      setCategory("Other");
    }
  }, [isOpen, submitted]);

  if (!isOpen) return null;

  const isLoading = contactQueryApi.loading;

  const issueTypes = [
    { value: "Bug", icon: Bug, color: "text-destructive" },
    { value: "Feature Request", icon: Lightbulb, color: "text-muted-foreground" },
    { value: "Other", icon: HelpCircle, color: "text-muted-foreground" },
  ];

  const selectedIssueType = issueTypes.find(type => type.value === category);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card text-card-foreground w-full max-w-md rounded-xl shadow-xl border border-border overflow-hidden max-h-[90vh] flex flex-col">

        {/* Compact Header */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/15 dark:to-primary/5 p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-primary rounded-lg">
                <MessageSquare className="h-4 w-4 text-primary-foreground" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">Contact Support</h2>
                <p className="text-xs text-muted-foreground">We're here to help</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              disabled={isLoading}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {submitted === "success" && (
            <div className="flex items-center gap-2 text-teal bg-teal-soft p-3 rounded-lg border border-teal/30">
              <div className="p-1 bg-teal-soft rounded-full">
                <CheckCircle className="h-4 w-4 text-teal" />
              </div>
              <div>
                <div className="text-sm font-medium">Message sent!</div>
                <div className="text-xs text-teal">We'll respond soon</div>
              </div>
            </div>
          )}

          {submitted === "error" && (
            <div className="flex items-center gap-2 text-destructive bg-destructive/10 p-3 rounded-lg border border-destructive/30">
              <div className="p-1 bg-destructive/10 rounded-full">
                <XCircle className="h-4 w-4 text-destructive" />
              </div>
              <div>
                <div className="text-sm font-medium">Failed to send</div>
                <div className="text-xs text-destructive">Please try again</div>
              </div>
            </div>
          )}

          {submitted === null && (
            <div className="space-y-4">
              {/* Email Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Mail className="h-3 w-3 text-primary" />
                  Your Email
                </label>
                <input
                  type="text"
                  value={email}
                  readOnly
                  className="w-full rounded-lg border border-input px-3 py-2 text-xs bg-muted text-muted-foreground font-medium"
                />
              </div>

              {/* Issue Category */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <AlertCircle className="h-3 w-3 text-primary" />
                  Issue Type
                </label>
                <div className="relative">
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded-lg border border-input px-3 py-2 text-xs appearance-none bg-background text-foreground pr-10 font-medium focus:border-ring focus:ring-2 focus:ring-ring/20 transition-all"
                    disabled={isLoading}
                  >
                    {issueTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.value}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    {selectedIssueType && (
                      <selectedIssueType.icon className={`h-4 w-4 ${selectedIssueType.color}`} />
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <MessageSquare className="h-3 w-3 text-primary" />
                  Description
                </label>
                <textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background text-foreground px-3 py-2 text-xs resize-none focus:border-ring focus:ring-2 focus:ring-ring/20 transition-all"
                  placeholder="Describe your issue in detail..."
                  disabled={isLoading}
                />
                <div className="flex justify-between items-center">
                  <div className="text-[10px] text-muted-foreground">
                    Be as detailed as possible
                  </div>
                  <div className={`text-[10px] ${description.length > 10 ? 'text-teal' : 'text-muted-foreground'}`}>
                    {description.length} chars
                  </div>
                </div>
              </div>

              {/* Help text */}
              <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/25 rounded-lg p-3">
                <div className="flex gap-2">
                  <div className="p-0.5 bg-primary/15 dark:bg-primary/20 rounded-full flex-shrink-0">
                    <HelpCircle className="h-3 w-3 text-primary" />
                  </div>
                  <div className="text-xs text-foreground">
                    <div className="font-medium mb-1">Tips for faster support:</div>
                    <ul className="text-[10px] space-y-0.5 text-muted-foreground">
                      <li>• Include browser & device info</li>
                      <li>• Expected vs actual behavior</li>
                      <li>• Mention error messages</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {submitted === null && (
          <div className="border-t border-border bg-muted/40 p-4">
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-xs font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className={`px-4 py-2 text-xs font-semibold text-primary-foreground rounded-lg transition-all flex items-center gap-1.5 ${
                  isLoading || !description.trim()
                    ? "bg-muted-foreground/60 cursor-not-allowed"
                    : "bg-primary hover:bg-primary/90 shadow-sm hover:shadow-md"
                }`}
                disabled={!description.trim() || isLoading}
              >
                {isLoading ? (
                  <svg
                    className="animate-spin h-3 w-3"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    ></path>
                  </svg>
                ) : (
                  <Send className="h-3 w-3" />
                )}
                {isLoading ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ContactModal;
